import { flattenDeep } from "lodash";
import { ModelType } from "~/enums/modelType";
import { ObjectHelper } from "~/helpers/objectHelper";
import { GenericType } from "~/interfaces/genericTypeInterfaces";
import { Hash } from "~/interfaces/hashInterfaces";
import { IImposter, IImposterFactory, IImposterOptions } from '~/interfaces/imposterInterfaces';
import DynamicInput from "~/models/dynamicInput";
import DynamicInputFactory from "~/models/dynamicInputFactory";
import ExternalConnectionEnvironmentAPIHeaderPair from "~/models/externalConnections/externalConnectionEnvironmentAPIHeaderPair";
import Imposter from "~/models/imposter";
import { RelationType } from "~/models/relationType";
import UntrackedModel from "~/models/untracked/untrackedModel";
import IRepo from "../../../../common/interfaces/repo";
import Repo from "../../../../common/state/modules/repo";
import EndPointProvider from "../../providers/connections/endPointProvider";

export default class EndPoint extends UntrackedModel implements IImposterFactory {
  static repo: IRepo<EndPoint, EndPointProvider> = new Repo<EndPoint, EndPointProvider>('connectionEndPoints', EndPoint, {}, new EndPointProvider(EndPoint, 'end_point', 'end_points'));
  repo: IRepo<this, EndPointProvider<this>>;
  static get provider(): EndPointProvider {
    return this.repo.provider;
  }
  get provider(): this['repo']['provider'] {
    return this.repo.provider;
  }

  get genericType(): GenericType {
    return ModelType.Object
  }

  rootModel: EndPoint;
  name: string;
  method: string;
  connection_id: string;
  test_header_pairs: Hash<ExternalConnectionEnvironmentAPIHeaderPair> = {};
  get testHeaderPairs(): ExternalConnectionEnvironmentAPIHeaderPair[] {
    return ObjectHelper.toArray(this.test_header_pairs)
  }
  live_header_pairs: Hash<ExternalConnectionEnvironmentAPIHeaderPair> = {};
  get liveHeaderPairs(): ExternalConnectionEnvironmentAPIHeaderPair[] {
    return ObjectHelper.toArray(this.live_header_pairs)
  }
  relative_url: string;
  except = ['createEmbeddedObject', 'saveTimer'];

  certificate_dynamic_inputs: Hash<DynamicInput> = {};
  response_dynamic_inputs: Hash<DynamicInput> = {};

  get certificateDynamicInputs(): DynamicInput[] {
    return ObjectHelper.toArray(this.certificate_dynamic_inputs)
      .sort((a, b) => a.sort_order - b.sort_order);
  }

  get responseDynamicInputs(): DynamicInput[] {
    return ObjectHelper.toArray(this.response_dynamic_inputs)
      .sort((a, b) => a.sort_order - b.sort_order);
  }

  get nextDynamicInputSortOrder(): number {
    const inputs = this.certificateDynamicInputs;
    if (inputs.length === 0) return 0;
    else return inputs[inputs.length - 1].sort_order + 1;
  }

  get nextResponseDynamicInputSortOrder(): number {
    const inputs = this.responseDynamicInputs;
    if (inputs.length === 0) return 0;
    else return inputs[inputs.length - 1].sort_order + 1;
  }

  recalculateDynamicInputPositions() {
    this.certificateDynamicInputs.forEach((it, index) => it.sort_order = index);
  }

  createEmbeddedObject = {
    certificate_dynamic_inputs: (parent, json) => {
      const certificateDynamicInput = DynamicInputFactory.create(this, json, false, true);
      certificateDynamicInput.except.push('id');
      return certificateDynamicInput
    },
    response_dynamic_inputs: (parent, json) => {
      const responseDynamicInput = DynamicInputFactory.create(this, json, false, true, true);
      responseDynamicInput.except.push('id');
      return responseDynamicInput
    },
    test_header_pairs: (parent, json): ExternalConnectionEnvironmentAPIHeaderPair => {
      return new ExternalConnectionEnvironmentAPIHeaderPair('test_header_pairs')
    },
    live_header_pairs: (parent, json): ExternalConnectionEnvironmentAPIHeaderPair => {
      return new ExternalConnectionEnvironmentAPIHeaderPair('live_header_pairs')
    },
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {
      'test_header_pairs': { type: ExternalConnectionEnvironmentAPIHeaderPair, relationType: RelationType.dictionary },
      'live_header_pairs': { type: ExternalConnectionEnvironmentAPIHeaderPair, relationType: RelationType.dictionary },
      'certificate_dynamic_inputs': { type: DynamicInput, relationType: RelationType.dictionary },
      'response_dynamic_inputs': { type: DynamicInput, relationType: RelationType.dictionary },
    };
  }

  constructor() {
    super();
  }

  static get displayName(): string {
    return "Endpoint";
  }

  get modelType(): ModelType {
    return ModelType.EndPoint
  }

  get referenceNameOrDefault(): string {
    return this.name;
  }

  get responseObjectArray(): DynamicInput[] {
    return flattenDeep(this.responseDynamicInputs.map(dynamicInput => dynamicInput.extractAllObjects())).map(di => {
      di['messageType'] = 'response';
      return di;
    })
  }

  get requestObjectArray(): DynamicInput[] {
    return flattenDeep(this.certificateDynamicInputs.map(dynamicInput => dynamicInput.extractAllObjects())).map(di => {
      di['messageType'] = 'request';
      return di;
    })
  }

  get responseInputArray(): DynamicInput[] {
    return flattenDeep(this.responseDynamicInputs.map(dynamicInput => dynamicInput.extractAllInputs()))
  }

  get requestInputArray(): DynamicInput[] {
    return flattenDeep(this.certificateDynamicInputs.map(dynamicInput => dynamicInput.extractAllInputs()))
  }

  imposterFor({ modelType, options }: { modelType: ModelType; options?: IImposterOptions }): IImposter {
    return new Imposter({
      ...ObjectHelper.sliceProperties(this as EndPoint, 'id', 'genericType', 'modelType'),
      isCollection: false,
    }, options);
  }

  get URLparameters() {
    const url = `${this.parent.live_environment.base_url}/${this.relative_url}`
    const parameterMatcher = /(\[\w+\])/gi 
    const parameters = url.match(parameterMatcher)
    return parameters ? parameters.map(parameter => parameter.replace(/(\[|\]|:|\/)/g, "")) : []
  }

  get allLiveHeaders() {
    let headers = this.liveHeaderPairs
    if (this.parent.test_environment.header_pairs) {
      headers = headers.concat(this.parent.test_environment.header_pairs)
    }
    return headers
  }
}